// Start tab: New / Open / import / About, and a master–detail list of CLIENTS (folders).
// Left: clients (newest activity first, filterable). Right: the selected client's scenarios
// (versions) — open one, open the latest, add a new one, or rename the client.
import { useEffect, useMemo, useState } from 'react';
import { act } from './actions';
import { Icon } from './Frame';
import { useDis } from './store';
import type { CaseRecord } from '../api/client';

const when = (s?: string): string => {
  if (!s) return '';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? s : d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const FolderIcon = () => <svg className="folder" viewBox="0 0 24 24" aria-hidden="true"><path d="M3 7h6l2 2h10v10H3z" /></svg>;
const GripIcon = () => <svg className="grip-svg" viewBox="0 0 24 24" aria-hidden="true"><circle cx="9" cy="6" r="1.4" /><circle cx="9" cy="12" r="1.4" /><circle cx="9" cy="18" r="1.4" /><circle cx="15" cy="6" r="1.4" /><circle cx="15" cy="12" r="1.4" /><circle cx="15" cy="18" r="1.4" /></svg>;

/** Latest-edited scenario of a loaded client (fallback: last in order). */
function latestScenario(scenarios: CaseRecord[]): CaseRecord | undefined {
  if (scenarios.length === 0) return undefined;
  return scenarios.reduce((a, b) => (String(b.updatedAt ?? '') > String(a.updatedAt ?? '') ? b : a), scenarios[0]);
}

export function StartPage() {
  const clients = useDis((s) => s.clientsList);
  const err = useDis((s) => s.clientsError);
  const loading = useDis((s) => s.clientsLoading);
  const refreshClients = useDis((s) => s.refreshClients);
  const loadClient = useDis((s) => s.loadClient);
  const openCase = useDis((s) => s.openCase);
  const newScenario = useDis((s) => s.newScenario);
  const renameClient = useDis((s) => s.renameClient);
  const reorderClients = useDis((s) => s.reorderClients);
  const reorderScenarios = useDis((s) => s.reorderScenarios);

  const [q, setQ] = useState('');
  const [sel, setSel] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [cDrag, setCDrag] = useState<string | null>(null);
  const [cOver, setCOver] = useState<string | null>(null);
  const [sDrag, setSDrag] = useState<string | null>(null);
  const [sOver, setSOver] = useState<string | null>(null);

  useEffect(() => { void refreshClients(); }, [refreshClients]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return clients;
    return clients.filter((c) => `${c.name} ${c.caseNumber} ${(c.partyNames ?? []).join(' ')}`.toLowerCase().includes(t));
  }, [clients, q]);

  // Keep a valid selection as the (filtered) list changes.
  useEffect(() => {
    if (filtered.length === 0) { setSel(null); return; }
    if (!sel || !filtered.some((c) => c.id === sel)) setSel(filtered[0].id);
  }, [filtered, sel]);

  // Load the selected client's scenarios.
  useEffect(() => { if (sel) void loadClient(sel); }, [sel, loadClient]);

  const detail = useDis((s) => (sel ? s.clientCache[sel] : undefined));
  const selMeta = filtered.find((c) => c.id === sel) ?? clients.find((c) => c.id === sel);
  const scenarios = detail?.scenarios ?? [];
  const latest = latestScenario(scenarios);

  function openLatest() {
    if (latest) void openCase(latest.id);
    else if (sel) void loadClient(sel, true).then(() => { const d = useDis.getState().clientCache[sel]; const l = d && latestScenario(d.scenarios); if (l) void openCase(l.id); });
  }
  function commitRename() {
    const v = draft.trim();
    if (v && sel && v !== selMeta?.name) { void renameClient(sel, v).then(() => void refreshClients()); }
    setEditing(false);
  }
  function addScenario() {
    if (!sel) return;
    const year = latest?.data?.taxYear;
    void newScenario({ clientId: sel, year }).then(() => void refreshClients());
  }

  // Reordering clients is only meaningful on the full, unfiltered list.
  const canReorderClients = q.trim() === '' && clients.length > 1;
  function dropClient(targetId: string) {
    if (!cDrag || cDrag === targetId) { setCDrag(null); setCOver(null); return; }
    const ids = clients.map((c) => c.id);
    const from = ids.indexOf(cDrag), to = ids.indexOf(targetId);
    setCDrag(null); setCOver(null);
    if (from < 0 || to < 0) return;
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    void reorderClients(ids);
  }
  function dropScn(targetId: string) {
    if (!sDrag || sDrag === targetId || !sel) { setSDrag(null); setSOver(null); return; }
    const ids = scenarios.map((s) => s.id);
    const from = ids.indexOf(sDrag), to = ids.indexOf(targetId);
    setSDrag(null); setSOver(null);
    if (from < 0 || to < 0) return;
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    void reorderScenarios(sel, ids);
  }

  return (
    <div className="startpg">
      <div>
        <h1>disOS</h1>
        <p className="lead">California guideline child and spousal support, in the desktop workflow you know.</p>
        <div className="acts">
          <button type="button" onClick={() => act('newCase')}><Icon name="new" />New case</button>
          <button type="button" onClick={() => act('openDlg')}><Icon name="open" />Open case…</button>
          <button type="button" onClick={() => act('qImport')}><Icon name="imp" />Import client questionnaire</button>
          <button type="button" onClick={() => act('about')}><Icon name="help" />About disOS</button>
        </div>
      </div>

      <div>
        <div className="card">
          <div className="card-head">
            <h2>Clients</h2>
            <span className="count">{clients.length > 0 ? `· ${clients.length} client${clients.length === 1 ? '' : 's'}` : ''}</span>
            <span className="search">
              <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4" /></svg>
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by name, number or party" aria-label="Filter clients" />
            </span>
          </div>

          {err ? (
            <div className="empty" style={{ color: 'var(--red)' }}>Could not load clients: {err}</div>
          ) : clients.length === 0 ? (
            <div className="empty">{loading ? 'Loading…' : 'No clients yet. “New case” starts one.'}</div>
          ) : filtered.length === 0 ? (
            <div className="empty">No clients match “{q}”.</div>
          ) : (
            <div className="md">
              <div className="md-list" role="listbox" aria-label="Clients">
                {filtered.map((c) => (
                  <div key={c.id} role="option" aria-selected={sel === c.id} tabIndex={0}
                    className={`mdc${sel === c.id ? ' on' : ''}${cDrag === c.id ? ' dragging' : ''}${cOver === c.id && cDrag && cDrag !== c.id ? ' drag-over' : ''}`}
                    draggable={canReorderClients}
                    onDragStart={canReorderClients ? (e) => { setCDrag(c.id); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', c.id); } : undefined}
                    onDragOver={(e) => { if (cDrag) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (cOver !== c.id) setCOver(c.id); } }}
                    onDrop={(e) => { if (cDrag) { e.preventDefault(); dropClient(c.id); } }}
                    onDragEnd={() => { setCDrag(null); setCOver(null); }}
                    onClick={() => setSel(c.id)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSel(c.id); } }}>
                    <span className={`row-grip${canReorderClients ? '' : ' off'}`} aria-hidden="true" title={canReorderClients ? 'Drag to reorder' : undefined}><GripIcon /></span>
                    <FolderIcon />
                    <div className="cmain">
                      <div className="cname">{c.name || 'Untitled'}</div>
                      <div className="mini">{(c.partyNames ?? []).filter(Boolean).join(' / ') || '—'}{c.caseNumber ? ` · ${c.caseNumber}` : ''}</div>
                    </div>
                    <span className="pillcount">{c.scenarioCount}</span>
                  </div>
                ))}
              </div>

              <div className="md-detail">
                {selMeta && (<>
                  {editing ? (
                    <input className="scn-rename" autoFocus value={draft}
                      onChange={(e) => setDraft(e.target.value)} onBlur={commitRename}
                      onKeyDown={(e) => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') setEditing(false); }} />
                  ) : (
                    <h3>{selMeta.name || 'Untitled'}</h3>
                  )}
                  <div className="sub">
                    {selMeta.caseNumber ? `${selMeta.caseNumber} · ` : ''}{(selMeta.partyNames ?? []).filter(Boolean).join(' / ') || '—'}
                    {!editing && <span className="pencil" role="button" tabIndex={0}
                      onClick={() => { setDraft(selMeta.name || ''); setEditing(true); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { setDraft(selMeta.name || ''); setEditing(true); } }}>✎ rename</span>}
                  </div>

                  {scenarios.length === 0 ? (
                    <div className="muted" style={{ fontSize: 12.5, padding: '6px 0' }}>Loading scenarios…</div>
                  ) : scenarios.map((s) => {
                    const isLatest = latest?.id === s.id;
                    const canDragScn = scenarios.length > 1;
                    return (
                      <div key={s.id} className={`mds${sDrag === s.id ? ' dragging' : ''}${sOver === s.id && sDrag && sDrag !== s.id ? ' drag-over' : ''}`} role="button" tabIndex={0}
                        draggable={canDragScn}
                        onDragStart={canDragScn ? (e) => { setSDrag(s.id); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', s.id); } : undefined}
                        onDragOver={(e) => { if (sDrag) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (sOver !== s.id) setSOver(s.id); } }}
                        onDrop={(e) => { if (sDrag) { e.preventDefault(); dropScn(s.id); } }}
                        onDragEnd={() => { setSDrag(null); setSOver(null); }}
                        onClick={() => void openCase(s.id)}
                        onKeyDown={(e) => { if (e.key === 'Enter') void openCase(s.id); }}>
                        <span className={`row-grip${canDragScn ? '' : ' off'}`} aria-hidden="true" title={canDragScn ? 'Drag to reorder' : undefined}><GripIcon /></span>
                        <span className="sdot" style={{ background: isLatest ? 'var(--accent)' : 'var(--faint)' }} />
                        <span className="slab">{s.label || 'Untitled scenario'}</span>
                        {isLatest && <span className="slatest">latest</span>}
                        <span className="swhen">{when(s.updatedAt)}</span>
                        <span className="op">Open →</span>
                      </div>
                    );
                  })}

                  <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                    <button type="button" className="mdnew" onClick={addScenario}>
                      <svg viewBox="0 0 24 24" width="15" height="15" style={{ stroke: 'currentColor', fill: 'none', strokeWidth: 1.8, strokeLinecap: 'round' }}><path d="M12 5v14M5 12h14" /></svg>
                      New scenario
                    </button>
                    {latest && <button type="button" className="openbtn" onClick={openLatest}>Open latest</button>}
                  </div>
                </>)}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
